import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    computed,
    inject,
    signal,
} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {
    catchError,
    combineLatest,
    filter,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import {
    AppValidators,
    switchMapWithLoading,
    HiddenValueWhileLoading,
    TRANSLATE,
    TranslatePipe,
} from '@brunata-angular/util';
import {
    ConfirmationDialogService,
    InputSelectOptionGroupComponent,
    InputTextAreaGroupComponent,
    InputTextGroupComponent,
    LoadComponent,
} from '@brunata-angular/ui';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {
    UserAdministrationService,
    AdminUser,
    UpdateUserRequest,
} from '@shared/services/user-administration.service';
import {SystemAccessPickerComponent} from '../system-access-picker/system-access-picker.component';
import {HasUnsavedChanges} from '@brunata-angular/ui';
import {selectUser} from 'src/app/state/selectors';
import {UserStateSuccess} from 'src/app/state/reducers';

type UserDetailView = {
    user: HiddenValueWhileLoading<AdminUser> | {loading: false; value: null};
    showSystemAccess: boolean;
};

@Component({
    selector: 'app-user-detail',
    standalone: true,
    imports: [
        AsyncPipe,
        LoadComponent,
        TranslatePipe,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputTextAreaGroupComponent,
        InputSelectOptionGroupComponent,
        SystemAccessPickerComponent,
    ],
    templateUrl: './user-detail.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailComponent implements HasUnsavedChanges {
    private readonly userService = inject(UserAdministrationService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly confirmationDialog = inject(ConfirmationDialogService);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);
    private readonly destroyRef = inject(DestroyRef);
    private readonly store = inject(Store);

    readonly roleOptions = new Map<string, string>([
        [
            'ADMINISTRATOR',
            this.translate('properties_roles.administrator', 'online', 'userAdministration'),
        ],
        ['USER', this.translate('properties_roles.user', 'online', 'userAdministration')],
    ]);

    readonly isSaving = signal(false);
    readonly assignedBuildingNos = signal<number[]>([]);

    // Deleting the user navigates away on purpose, so the unsaved-changes guard must not
    // challenge that programmatic navigation even if the form was left dirty.
    private leaving = false;

    private readonly loginNameId$ = this.route.params.pipe(
        map(params => Number(params['loginNameId'])),
        tap(() => {
            this.assignedBuildingNos.set([]);
            this.formGroup.reset();
        }),
        shareReplay(1),
    );

    readonly formGroup = this.fb.group({
        personName: ['', Validators.required],
        userName: [{value: '', disabled: true}],
        email: ['', [Validators.required, AppValidators.email]],
        role: ['', Validators.required],
        note: ['', Validators.maxLength(100)],
    });

    private readonly roleValue$ = this.formGroup.controls.role.valueChanges.pipe(
        startWith(this.formGroup.controls.role.value),
        shareReplay(1),
    );

    private readonly user$ = this.loginNameId$.pipe(
        switchMapWithLoading(loginNameId => this.userService.getUser(loginNameId)),
        shareReplay(1),
    );

    readonly view$: Observable<UserDetailView> = combineLatest([this.user$, this.roleValue$]).pipe(
        map(
            ([userResponse, roleValue]): UserDetailView => ({
                user: userResponse,
                showSystemAccess: roleValue !== 'ADMINISTRATOR',
            }),
        ),
        catchError(() =>
            of({user: {loading: false as const, value: null}, showSystemAccess: false}),
        ),
    );

    // The forgot-password action is only offered when the *logged-in* user is an administrator
    // (the main account or an admin sub-user); regular users must not see it.
    private readonly currentUserRole = toSignal(
        this.store.select(selectUser).pipe(
            filter((state): state is UserStateSuccess => state.status === 'success'),
            map(state => state.user.role),
        ),
    );
    protected readonly canResetPassword = computed(
        () => this.currentUserRole() === 'ADMINISTRATOR',
    );

    constructor() {
        this.user$
            .pipe(
                filter(
                    (response): response is {loading: false; value: AdminUser} => !response.loading,
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(response => {
                this.formGroup.patchValue({
                    personName: response.value.personName ?? '',
                    userName: response.value.userName,
                    email: response.value.email ?? '',
                    role: response.value.role,
                    note: response.value.note ?? '',
                });
                this.formGroup.markAsPristine();
            });

        // Seed the picker with the user's current building access.
        this.loginNameId$
            .pipe(
                switchMap(loginNameId =>
                    this.userService
                        .getUserBuildingAccess(loginNameId)
                        .pipe(catchError(() => of<string[]>([]))),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(accessNos => this.assignedBuildingNos.set(accessNos.map(Number)));
    }

    saveChanges(loginNameId: number): void {
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }
        if (this.isSaving()) {
            return;
        }

        const formValue = this.formGroup.getRawValue();
        const isAdministrator = formValue.role.toUpperCase() === 'ADMINISTRATOR';
        const updateRequest: UpdateUserRequest = {
            personName: formValue.personName,
            email: formValue.email,
            role: formValue.role,
            note: formValue.note,
        };

        const buildingNos = this.assignedBuildingNos().map(String);

        this.isSaving.set(true);
        this.userService
            .updateUser(loginNameId, updateRequest)
            .pipe(
                switchMap(updatedUser =>
                    // Administrators implicitly have access to all systems, so skip the
                    // building-access write for them (mirrors create-user).
                    isAdministrator
                        ? of(updatedUser)
                        : this.userService
                              .updateUserBuildingAccess(loginNameId, buildingNos)
                              .pipe(map(() => updatedUser)),
                ),
            )
            .subscribe({
                next: () => {
                    this.isSaving.set(false);
                    this.toastr.success(
                        this.translate(
                            'alerts_updateUserAccessSuccess',
                            'online',
                            'userAdministration',
                        ),
                    );
                    this.formGroup.markAsPristine();
                },
                // The error toast is shown by the global ErrorInterceptor; leaving the form
                // dirty lets the user retry the save.
                error: () => this.isSaving.set(false),
            });
    }

    async forgotPassword(loginNameId: number): Promise<void> {
        const confirmed = await this.confirmationDialog.confirm(
            this.translate('modals_forgotPassword.title', 'online', 'userAdministration'),
            this.translate('modals_forgotPassword.content', 'online', 'userAdministration'),
            this.translate('actions_confirm', 'online', 'common'),
        );
        if (!confirmed) {
            return;
        }

        this.userService.resetPassword(loginNameId).subscribe({
            next: () => {
                this.toastr.success(
                    this.translate('alerts_forgotPasswordSuccess', 'online', 'userAdministration'),
                );
            },
            // The error toast is shown by the global ErrorInterceptor.
            error: () => {},
        });
    }

    async deleteUser(loginNameId: number, fullName: string): Promise<void> {
        const content = this.translate(
            'modals_deleteModal.content',
            'online',
            'userAdministration',
            // The placeholder's inner spacing differs between language bundles
            // ({{fullName}} vs {{ fullName }}), so match it whitespace-tolerantly. A replacer
            // function avoids a `$` in the name being read as a replacement pattern.
        ).replace(/\{\{\s*fullName\s*\}\}/g, () => `<strong>${fullName}</strong>`);
        const confirmed = await this.confirmationDialog.confirm(
            this.translate('modals_deleteModal.title', 'online', 'userAdministration'),
            content,
            this.translate('actions_deleteUser', 'online', 'userAdministration'),
        );
        if (!confirmed) {
            return;
        }

        this.userService.deleteUser(loginNameId).subscribe({
            next: () => {
                this.leaving = true;
                this.toastr.success(
                    this.translate('alerts_deleteUserSuccess', 'online', 'userAdministration'),
                );
                void this.navigateBack();
            },
            error: () => {},
        });
    }

    hasUnsavedChanges(): boolean {
        return !this.leaving && this.formGroup.dirty;
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }

    private async navigateBack(): Promise<void> {
        await this.router.navigate(['../'], {relativeTo: this.route});
    }
}
